import { type MutableRefObject, type RefCallback, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import {
  areWorkspaceHeaderSearchLayoutsEqual,
  getWorkspaceHeaderSearchLayout,
  type WorkspaceHeaderSearchLayout,
} from '~/components/v2/components/workspace/workspace-header/utils/getWorkspaceHeaderSearchLayout';

type HeaderMeasureNodes = {
  actions?: HTMLDivElement;
  header?: HTMLElement;
  searchSlot?: HTMLDivElement;
  workspaceInfo?: HTMLDivElement;
};

type ObserveMeasuredNodeParams<Element extends HTMLElement> = {
  measure: () => void;
  node: Element | null;
  observerRef: MutableRefObject<ResizeObserver | undefined>;
  previous: Element | undefined;
};

const readHorizontalPaddingPx = (element: HTMLElement) => {
  const styles = getComputedStyle(element);
  const paddingPx = Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight);

  if (!Number.isFinite(paddingPx)) {
    return 0;
  }

  return Math.ceil(paddingPx);
};

const observeMeasuredNode = <Element extends HTMLElement>({
  measure,
  node,
  observerRef,
  previous,
}: ObserveMeasuredNodeParams<Element>) => {
  if (previous) {
    observerRef.current?.unobserve(previous);
  }

  if (!node) {
    return undefined;
  }

  const observer = observerRef.current ?? new ResizeObserver(() => measure());
  observerRef.current = observer;
  observer.observe(node);
  measure();

  return node;
};

export const useWorkspaceHeaderSearchLayout = () => {
  const nodesRef = useRef<HeaderMeasureNodes>({});
  const observerRef = useRef<ResizeObserver>();
  const [layout, setLayout] = useState<WorkspaceHeaderSearchLayout>({ mode: 'natural' });
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const measure = useCallback(() => {
    const { actions, header, searchSlot, workspaceInfo } = nodesRef.current;

    if (!header || !workspaceInfo || !actions || !searchSlot) {
      return;
    }

    const nextLayout = getWorkspaceHeaderSearchLayout(
      {
        actionsWidthPx: actions.getBoundingClientRect().width,
        headerWidthPx: header.clientWidth,
        searchSlotPaddingPx: readHorizontalPaddingPx(searchSlot),
        workspaceInfoWidthPx: workspaceInfo.getBoundingClientRect().width,
      },
      layoutRef.current,
    );

    setLayout((current) => (areWorkspaceHeaderSearchLayoutsEqual(current, nextLayout) ? current : nextLayout));
  }, []);

  const headerRef = useCallback<RefCallback<HTMLElement>>(
    (node) => {
      nodesRef.current.header = observeMeasuredNode({
        measure,
        node,
        observerRef,
        previous: nodesRef.current.header,
      });
    },
    [measure],
  );

  const workspaceInfoRef = useCallback<RefCallback<HTMLDivElement>>(
    (node) => {
      nodesRef.current.workspaceInfo = observeMeasuredNode({
        measure,
        node,
        observerRef,
        previous: nodesRef.current.workspaceInfo,
      });
    },
    [measure],
  );

  const actionsRef = useCallback<RefCallback<HTMLDivElement>>(
    (node) => {
      nodesRef.current.actions = observeMeasuredNode({
        measure,
        node,
        observerRef,
        previous: nodesRef.current.actions,
      });
    },
    [measure],
  );

  const searchSlotRef = useCallback<RefCallback<HTMLDivElement>>(
    (node) => {
      nodesRef.current.searchSlot = observeMeasuredNode({
        measure,
        node,
        observerRef,
        previous: nodesRef.current.searchSlot,
      });
    },
    [measure],
  );

  useIsomorphicLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
    },
    [],
  );

  return { actionsRef, headerRef, layout, searchSlotRef, workspaceInfoRef };
};
